/*
 * ----------------------------------------------------------------------------
 * ZAHNHELD HOME MOTION LAYER (homepage only)
 * ----------------------------------------------------------------------------
 * Init layer on top of the vendored Motion engine (assets/motion.js, MIT).
 * Ported from the zahnheld-startseite prototype (index-v3.html) with the theme
 * adaptations documented per module. Companions: assets/zh-home-motion.css,
 * snippets/zh-motion-config.liquid (pre-paint gate + toggle map),
 * snippets/zh-motion-overlays.liquid (fixed page-level overlays).
 *
 * The page load curtain used to live here. It is now its own sitewide capable
 * section (sections/zh-preloader.liquid, assets/zh-preloader.js) and hands over
 * with a `zh:preloader:done` event, see the hero intro at the bottom of this file.
 *
 * Contracts:
 *  - Runs only when <html> carries `zh-home-motion` (set pre-paint by the config
 *    snippet). If the Motion engine failed to load, the class is REMOVED so all
 *    CSS-scoped initial states dissolve and content stays visible.
 *  - Only shorthand transform props are animated (x, y, scale, scaleX, scaleY,
 *    opacity, rotate) — this Motion build does not persist `transform` string or
 *    `clip-path` keyframes. Persistent states use CSS classes.
 *  - JS-applied hidden initial states only ever target below-the-fold elements
 *    (below() helper), so above-fold content is never hidden by JS.
 *  - Every module element is marked via data-zhmInit so the re-init on
 *    shopify:section:load (theme editor) is idempotent.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  if (!root.classList.contains('zh-home-motion')) {
    return;
  }

  if (!window.Motion) {
    // Engine blocked/missing: dissolve all CSS initial states, show everything.
    // The curtain is not our business any more: it lives in the preloader section,
    // is engine free and takes itself down on its own.
    root.classList.remove('zh-home-motion');
    return;
  }

  var Motion = window.Motion;
  var animate = Motion.animate;
  var scroll = Motion.scroll;
  var inView = Motion.inView;
  var stagger = Motion.stagger;

  var EXPO = [0.16, 1, 0.3, 1]; // heavy decel, agency-style
  var CURTAIN = [0.83, 0, 0.17, 1]; // symmetric power curtain
  var BACK = [0.34, 1.56, 0.64, 1]; // overshoot pop
  // The spawn's power2.out lives in zh-home-motion.css now, with its cascade.

  var FINE = window.matchMedia('(pointer: fine)').matches;

  /* A count-up that is meant to be read, not admired. EXPO clears 80% of the distance
     in 0.41s of its 1.8s. That is right for the six digit figures on the homepage stats
     band, where the churn itself is the effect, and useless for a two digit value: it is
     simply over. This curve keeps the number moving for about 1.6s (50% at 0.5s, 80% at
     0.9s) and still lands softly. */
  var COUNT_READ = [0.4, 0.5, 0.45, 1];
  var COUNT_READ_DURATION = 2.4;

  // Per-module toggles from the theme settings (zh-motion-config.liquid).
  var config = {};
  try {
    config = JSON.parse(document.getElementById('zh-motion-config').textContent);
  } catch (e) {
    config = {};
  }
  var enabled = function (key) {
    return config[key] !== false;
  };

  // The hero intro parameters (direction, stagger, media zoom) belong to the
  // preloader section, which owns the whole page load choreography so a merchant
  // tunes the sequence in one place. See sections/zh-preloader.liquid.
  var intro = window.zhPreloaderConfig || {};

  /* ---------- helpers ---------- */

  var $$ = function (sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  };

  // Only elements starting below the fold get hidden initial states.
  var below = function (el) {
    return el.getBoundingClientRect().top > window.innerHeight * 0.86;
  };

  var preHide = function (el, transform) {
    el.style.opacity = '0';
    if (transform) {
      el.style.transform = transform;
    }
  };

  // Idempotency guard for shopify:section:load re-inits.
  var once = function (el, key) {
    var flag = 'zhmInit' + key;
    if (el.dataset[flag]) {
      return false;
    }
    el.dataset[flag] = '1';
    return true;
  };

  /* ---------- module registry (each runs scoped, idempotent) ---------- */

  var modules = {};

  /* Masked headline lines: sections render .zhm-line > .zhm-line__in server-side. */
  modules.mask = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="mask"]', scope).forEach(function (h) {
      if (!once(h, 'Mask')) return;
      if (!below(h)) return;
      var inners = $$('.zhm-line__in', h);
      if (!inners.length) return;
      inners.forEach(function (i) {
        i.style.transform = 'translateY(112%)';
      });
      inView(h, function () {
        animate(inners, { y: ['112%', '0%'] }, { duration: 1, ease: EXPO, delay: stagger(0.11) });
      }, { margin: '0px 0px -10% 0px' });
    });
  };

  /* Single fade-up reveals. Optional data-zhm-dist overrides the 36px distance. */
  modules.reveal = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="reveal"]', scope).forEach(function (el) {
      if (!once(el, 'Reveal')) return;
      if (!below(el)) return;
      var dist = parseFloat(el.getAttribute('data-zhm-dist')) || 36;
      preHide(el, 'translateY(' + dist + 'px)');
      inView(el, function () {
        animate(el, { opacity: [0, 1], y: [dist, 0] }, { duration: 0.9, ease: EXPO });
      }, { margin: '0px 0px -10% 0px' });
    });
  };

  /* Staggered group reveals: container [data-zhm="group"], children [data-zhm-item]. */
  modules.group = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="group"]', scope).forEach(function (c) {
      if (!once(c, 'Group')) return;
      var kids = $$('[data-zhm-item]', c).filter(below);
      if (!kids.length) return;
      kids.forEach(function (k) {
        preHide(k, 'translateY(44px)');
      });
      inView(c, function () {
        animate(kids, { opacity: [0, 1], y: [44, 0] }, { duration: 0.85, ease: EXPO, delay: stagger(0.09) });
      }, { margin: '0px 0px -8% 0px' });
    });
  };

  /* Collection teaser: cards build up, media pushes in from 1.28x. After the
     push-in the inline transform is cleared so the CSS hover zoom takes over. */
  modules.cards = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="cards"]', scope).forEach(function (g) {
      if (!once(g, 'Cards')) return;
      var cards = $$('[data-zhm-card]', g).filter(below);
      if (!cards.length) return;
      cards.forEach(function (c) {
        c.style.opacity = '0';
        var m = c.querySelector('[data-zhm-card-media]');
        if (m) m.style.transform = 'scale(1.28)';
      });
      inView(g, function () {
        cards.forEach(function (c, i) {
          animate(c, { opacity: [0, 1], y: [54, 0] }, { duration: 1, ease: EXPO, delay: i * 0.13 });
          var m = c.querySelector('[data-zhm-card-media]');
          if (m) {
            animate(m, { scale: [1.28, 1] }, { duration: 1.3, ease: EXPO, delay: i * 0.13 }).finished.then(function () {
              m.style.transform = '';
            });
          }
        });
      }, { margin: '0px 0px -12% 0px' });
    });
  };

  /* Split reveal: two black halves part with a light seam. The cover only exists
     under the motion class (CSS), so content is never blocked without JS. */
  modules.split = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="split"]', scope).forEach(function (sp) {
      if (!once(sp, 'Split')) return;
      var halves = $$('.zhm-split__half', sp);
      if (!halves.length) return;
      if (!below(sp)) {
        // Already in view: parting would flash. Drop the cover instantly.
        var cover = sp.querySelector('.zhm-split__cover');
        if (cover) cover.style.display = 'none';
        return;
      }
      inView(sp, function () {
        var cover = sp.querySelector('.zhm-split__cover');
        var part = animate(halves, { scaleX: [1, 0] }, { duration: 1.1, ease: CURTAIN, delay: 0.3 });
        // Remove the whole cover once parted, so nothing is left over the image.
        if (part && part.finished) {
          part.finished.then(function () {
            if (cover) cover.style.display = 'none';
          });
        }
      }, { margin: '0px 0px -18% 0px' });
    });
  };

  /* Image spawn: three-phase entrance for the zh-editorial media.
       1) congruent cards grow out of the centre of the whole SECTION, staggered
       2) the stack travels as one group into the media column
       3) the copy column reveals, overlapping the tail of the flight
     The frame drops its clipping for phases 1 and 2 (.is-flying) so the centred
     stack is not cut off; no layout property is touched. Cards are emitted
     bottom-of-stack first, so animating in DOM order ends on the primary image
     sitting on top, which is also the state the section renders without JS.

     This runs as CSS animations (zh-home-motion.css), for the same reason the hero
     intro does. The Motion build vendored here has no WAAPI path at all — measured
     on the page, animate() leaves getAnimations() empty and writes an inline style
     per frame — so six full width images scaling at once was six style recalcs and
     six re-rasters per frame on the main thread, next to the app scripts. This
     module keeps the whole choreography (every timing is here) and stamps it onto
     the elements; the CSS owns only what moves and how it eases.

     Initial states also come from CSS now, via .is-spawn-armed. They used to be
     inline styles written at init, which is a second place that had to agree with
     the animation about what "hidden" means.

     The travel distance is measured when the section reveals, NOT at init: the
     layout at init is not the layout the flight starts from (fonts, lazy media, the
     curtain), and a stale measurement is what could send the stack off towards the
     page edge. Stacked viewports do not fly at all — there the media and the
     section share a centre, so a measured offset can only be layout noise. */
  var SPAWN = {
    delay: 0.25,
    stagger: 0.13,
    duration: 0.7,
    flight: 1,
    copy: 0.35,
    copyStagger: 0.09,
    copyDuration: 0.85
  };

  var SPAWN_STACKED = 900; // the section's own single-column breakpoint
  var SPAWN_MAX_OFFSET = 120; // % of the frame width; past that it is not a measurement

  /**
   * Distance from the frame's centre to the section's centre, in percent of the
   * frame's own width, or 0 when the stack must not travel at all. Measured rather
   * than assumed, which covers the reversed layout (media in the second column).
   *
   * Stacked viewports never travel: there the frame and the section share a centre,
   * so any offset can only be layout noise. Neither does a frame without a width or
   * one whose offset is past every plausible layout — both mean the page was caught
   * mid reflow, and acting on that is what once sent the stack towards the edge.
   *
   * @param {HTMLElement} media   - the media frame
   * @param {HTMLElement} section - the editorial section around it
   * @returns {number} offset in percent of the frame width, 0 for "stay put"
   */
  var spawnOffset = function (media, section) {
    if (!section || window.innerWidth < SPAWN_STACKED) return 0;

    var m = media.getBoundingClientRect();
    var s = section.getBoundingClientRect();
    if (m.width <= 0) return 0;

    var offset = Math.round(((s.left + s.width / 2 - (m.left + m.width / 2)) / m.width) * 1000) / 10;
    if (Math.abs(offset) <= 0.5 || Math.abs(offset) > SPAWN_MAX_OFFSET) return 0;

    return offset;
  };

  modules.spawn = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="spawn"]', scope).forEach(function (media) {
      if (!once(media, 'Spawn')) return;
      var stack = media.querySelector('.zhm-spawn__stack');
      if (!stack) return;
      var cards = $$('.zhm-spawn__card', stack);
      if (!cards.length) return;
      if (!below(media)) return;

      var section = media.closest('.zh-ed');
      var copy = section ? $$('[data-zhm-spawn-copy]', section) : [];
      var host = section || media;

      host.classList.add('is-spawn-armed');

      var stamp = function (el, delay, duration) {
        el.style.animationDelay = Math.round(delay * 1000) + 'ms';
        el.style.animationDuration = Math.round(duration * 1000) + 'ms';
      };

      inView(media, function () {
        // Read before anything is stamped: getBoundingClientRect reports the
        // transformed box, and this is the layout the flight starts from.
        var offset = spawnOffset(media, section);
        var travels = offset !== 0;

        var cardsLanded = SPAWN.delay + (cards.length - 1) * SPAWN.stagger + SPAWN.duration;
        var copyStarts = cardsLanded + SPAWN.copy;

        cards.forEach(function (c, i) {
          stamp(c, SPAWN.delay + i * SPAWN.stagger, SPAWN.duration);
        });

        if (travels) {
          stack.style.setProperty('--zhm-spawn-x', offset + '%');
          stamp(stack, cardsLanded, SPAWN.flight);
          // Also what scopes the flight animation, so a stack that does not travel
          // carries no animation and no layer at all.
          media.classList.add('is-flying');
        }

        copy.forEach(function (el, i) {
          stamp(el, copyStarts + i * SPAWN.copyStagger, SPAWN.copyDuration);
        });

        host.classList.add('is-spawning');

        /* Both hand-backs below run on the animation clock, never on a timer. A
           hidden tab freezes CSS animations while timeouts keep firing, so a wall
           clock drops the classes with the cards still sitting at scale(0) and the
           whole section snaps into place. Same lesson as the preloader's shared
           clock, one layer down.

           `finished` also settles on rejection, i.e. when an animation is cancelled
           (theme editor reload). Nothing may leave the section stranded behind a
           hidden state, so every exit path hands back. */
        var landing = function (els) {
          var pending = [];
          els.forEach(function (el) {
            if (!el.getAnimations) return;
            el.getAnimations().forEach(function (a) {
              if (a.finished && String(a.animationName).indexOf('zhm-spawn') === 0) {
                pending.push(a.finished);
              }
            });
          });
          return pending;
        };

        var onceLanded = function (pending, done) {
          if (pending.length) Promise.all(pending).then(done, done);
          else done(); // no animations at all (reduced motion): nothing to wait for
        };

        if (travels) {
          // Restore the frame's clipping only once the stack is home again.
          onceLanded(landing([stack]), function () {
            media.classList.remove('is-flying');
          });
        }

        // Everything has landed: drop the animations and their layers. The resting
        // state is what the keyframes end on, so this is not a visible change.
        onceLanded(landing(cards.concat(copy)), function () {
          host.classList.remove('is-spawn-armed');
          host.classList.remove('is-spawning');
        });
      }, { margin: '0px 0px -18% 0px' });
    });
  };

  /* Nested collage: base image fades from 1.15x, framed layers pop from 0.55. */
  modules.nested = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="nested"]', scope).forEach(function (n) {
      if (!once(n, 'Nested')) return;
      if (!below(n)) return;
      var l1 = n.querySelector('.zhm-nested__layer--1');
      var l2 = n.querySelector('.zhm-nested__layer--2');
      var l3 = n.querySelector('.zhm-nested__layer--3');
      if (l1) l1.style.opacity = '0';
      [l2, l3].forEach(function (x) {
        if (x) {
          x.style.opacity = '0';
          x.style.transform = 'scale(.55)';
        }
      });
      inView(n, function () {
        if (l1) animate(l1, { opacity: [0, 1], scale: [1.15, 1] }, { duration: 1, ease: EXPO });
        if (l2) animate(l2, { opacity: [0, 1], scale: [0.55, 1] }, { duration: 0.9, ease: EXPO, delay: 0.45 });
        if (l3) animate(l3, { opacity: [0, 1], scale: [0.55, 1] }, { duration: 0.9, ease: EXPO, delay: 0.65 });
      }, { margin: '0px 0px -14% 0px' });
    });
  };

  /* Comparison table: row cascade + verdict-mark pop. */
  modules.cmp = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="cmp"]', scope).forEach(function (table) {
      if (!once(table, 'Cmp')) return;
      $$('tbody tr', table).forEach(function (tr, i) {
        if (!below(tr)) return;
        tr.style.opacity = '0';
        inView(tr, function () {
          animate(tr, { opacity: [0, 1] }, { duration: 0.6, ease: EXPO, delay: (i % 6) * 0.09 });
        }, { margin: '0px 0px -6% 0px' });
      });
      $$('.zhm-pop', table).forEach(function (mk, i) {
        if (!below(mk)) return;
        mk.style.transform = 'scale(0)';
        inView(mk, function () {
          animate(mk, { scale: [0, 1] }, { duration: 0.55, ease: BACK, delay: 0.25 + (i % 8) * 0.07 });
        }, { margin: '0px 0px -6% 0px' });
      });
    });
  };

  /* Promo panels: split entrance from both sides (stacked viewports: from below). */
  modules.promo = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="promo"]', scope).forEach(function (p) {
      if (!once(p, 'Promo')) return;
      var panels = $$('[data-zhm-panel]', p).filter(below);
      if (!panels.length) return;
      var stacked = window.innerWidth < 900;
      panels.forEach(function (pl, i) {
        preHide(pl, stacked ? 'translateY(56px)' : 'translateX(' + (i ? 64 : -64) + 'px)');
      });
      inView(p, function () {
        panels.forEach(function (pl, i) {
          animate(
            pl,
            stacked ? { opacity: [0, 1], y: [56, 0] } : { opacity: [0, 1], x: [i ? 64 : -64, 0] },
            { duration: 1, ease: EXPO, delay: i * 0.12 }
          );
        });
      }, { margin: '0px 0px -12% 0px' });
    });
  };

  /* Drop statement: words fall in from above with overshoot through masks. */
  modules.drop = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="drop"]', scope).forEach(function (sec) {
      if (!once(sec, 'Drop')) return;
      var ws = $$('.zhm-dw__in', sec);
      if (!ws.length || !below(sec)) return;
      ws.forEach(function (w) {
        w.style.transform = 'translateY(-130%)';
      });
      inView(sec, function () {
        animate(ws, { y: ['-130%', '0%'] }, { duration: 0.85, ease: BACK, delay: stagger(0.09) });
      }, { margin: '0px 0px -12% 0px' });
    });
  };

  /* Product launch: title mask lines, media push-in, extras fade, pills pop. */
  modules.launch = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="launch"]', scope).forEach(function (sec) {
      if (!once(sec, 'Launch')) return;
      if (!below(sec)) return;
      var lines = $$('.zhm-line__in', sec);
      var img = sec.querySelector('[data-zhm-launch-media]');
      var pills = $$('.zhm-pop', sec);
      var extra = $$('[data-zhm-launch-extra]', sec);
      lines.forEach(function (l) {
        l.style.transform = 'translateY(112%)';
      });
      if (img) {
        img.style.opacity = '0';
        img.style.transform = 'scale(1.22)';
      }
      pills.forEach(function (p) {
        p.style.opacity = '0';
        p.style.transform = 'scale(0)';
      });
      extra.forEach(function (e) {
        e.style.opacity = '0';
      });
      inView(sec, function () {
        if (lines.length) animate(lines, { y: ['112%', '0%'] }, { duration: 0.95, ease: EXPO, delay: stagger(0.12) });
        if (img) animate(img, { opacity: [0, 1], scale: [1.22, 1] }, { duration: 1.2, ease: EXPO });
        if (extra.length) animate(extra, { opacity: [0, 1], y: [18, 0] }, { duration: 0.8, ease: EXPO, delay: stagger(0.08, { startDelay: 0.25 }) });
        if (pills.length) animate(pills, { opacity: [0, 1], scale: [0, 1.15, 1] }, { duration: 0.55, ease: BACK, delay: stagger(0.08, { startDelay: 0.5 }) });
      }, { margin: '0px 0px -14% 0px' });
    });
  };

  /* Star pop: sections render each ★ as a .zhm-star span server-side. */
  modules.stars = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="stars"]', scope).forEach(function (st) {
      if (!once(st, 'Stars')) return;
      var spans = $$('.zhm-star', st);
      if (!spans.length || !below(st)) return;
      spans.forEach(function (s) {
        s.style.opacity = '0';
        s.style.transform = 'scale(0)';
      });
      inView(st, function () {
        animate(spans, { opacity: [0, 1], scale: [0, 1.3, 1] }, { duration: 0.6, ease: BACK, delay: stagger(0.07) });
      }, { margin: '0px 0px -6% 0px' });
    });
  };

  /* Count-up on the element's leading text node; German number formatting
     (1.000er-Punkte, Komma-Dezimalen) parsed from and restored to the markup,
     so the server-rendered value is always the source of truth.

     Two values do not get animated at all. A range ("3-6 bar") is not a quantity:
     only its leading digit would move, so halfway through it reads "0-6 bar".

     An element marked data-zhm-pace="read" runs on COUNT_READ instead. That is what a
     small value inside a revealing group needs: modules.group fades its card in over
     0.85s while the default curve is 80% done in 0.41s, so the count was over before
     the card was legible. The slower curve is barely a tenth of the way in when the
     card lands and then keeps moving for well over a second.

     Deliberately no head start on top of that. Writing the start value early takes the
     snap out of it, but the zero then stands in the DOM until someone scrolls, and a
     rendering crawler is exactly such a stopped frame. Same reason the review score in
     snippets/zh-plp-rating.liquid does not count at all. */
  modules.countup = function (scope) {
    if (!enabled('countups')) return;
    $$('[data-zhm="countup"]', scope).forEach(function (el) {
      if (!once(el, 'Count')) return;
      var node = el.childNodes[0];
      if (!node || node.nodeType !== 3) return;
      var raw = node.nodeValue;
      var m = raw.match(/(\d[\d.]*(?:,\d+)?)/);
      if (!m) return;
      var numStr = m[1];
      var dec = (numStr.split(',')[1] || '').length;
      var target = parseFloat(numStr.replace(/\./g, '').replace(',', '.'));
      var pre = raw.slice(0, m.index);
      var suf = raw.slice(m.index + numStr.length);
      if (/^\s*[-\u2013]\s*\d/.test(suf)) return;
      var fmt = function (v) {
        return dec ? v.toFixed(dec).replace('.', ',') : Math.round(v).toLocaleString('de-DE');
      };
      if (!below(el)) return;
      var paced = el.dataset.zhmPace === 'read';
      inView(el, function () {
        animate(0, target, {
          duration: paced ? COUNT_READ_DURATION : 1.8,
          ease: paced ? COUNT_READ : EXPO,
          onUpdate: function (v) {
            node.nodeValue = pre + fmt(v) + suf;
          }
        });
      }, { margin: '0px 0px -10% 0px' });
    });
  };

  /* Star fill for the LP quotes: the rating sweeps in from the left.

     The LP renders its stars as one background-clip gradient with a hard stop at
     --fill, not as five separate glyphs, so modules.stars has nothing to pop here.
     Animating the stop keeps fractional ratings, which five discrete spans would
     have cost. The property is driven from a plain number animation rather than
     handed to the engine as a custom property: an unregistered --fill has no type the
     browser could interpolate.

     The stop is emptied inside the callback, not when the element is armed, so an
     unscrolled page never holds a row of empty stars. */
  modules.starFill = function (scope) {
    if (!enabled('reveals')) return;
    $$('[data-zhm="star-fill"]', scope).forEach(function (el) {
      if (!once(el, 'StarFill')) return;
      var target = parseFloat(el.style.getPropertyValue('--fill'));
      if (!isFinite(target) || target <= 0) return;
      if (!below(el)) return;
      inView(el, function () {
        el.style.setProperty('--fill', '0%');
        animate(0, target, {
          duration: 1.1,
          ease: EXPO,
          onUpdate: function (v) {
            el.style.setProperty('--fill', v + '%');
          }
        });
      }, { margin: '0px 0px -6% 0px' });
    });
  };

  /* Hero plakat parallax: drifts up and fades while the hero scrolls out. */
  modules.heroParallax = function (scope) {
    if (!enabled('parallax') || !FINE) return;
    $$('[data-zhm="hero-parallax"]', scope).forEach(function (el) {
      if (!once(el, 'HeroPar')) return;
      var section = el.closest('.shopify-section') || el.parentElement;
      scroll(
        animate(el, { y: [0, -90], opacity: [1, 0.25] }, { ease: 'linear' }),
        { target: section, offset: ['start start', 'end start'] }
      );
    });
  };

  /* Editorial media parallax: inner child drifts inside the overflow-clipped frame. */
  modules.mediaParallax = function (scope) {
    if (!enabled('parallax') || !FINE) return;
    $$('[data-zhm="media-parallax"]', scope).forEach(function (mEl) {
      if (!once(mEl, 'MediaPar')) return;
      var inner = mEl.firstElementChild;
      if (!inner) return;
      scroll(
        animate(inner, { y: ['-6%', '6%'], scale: [1.14, 1.14] }, { ease: 'linear' }),
        { target: mEl, offset: ['start end', 'end start'] }
      );
    });
  };

  /* Track tagline: kinetic type moves with scroll.

     The move itself is a scroll-driven CSS animation (zh-home-motion.css) and
     therefore runs on the compositor. It used to be an rAF mapping here that read
     getBoundingClientRect and wrote a transform on every scroll frame, i.e. a main
     thread animation — the same trade the hero intro already made, and for the same
     reason: on mobile the app scripts take the frames.

     What is left for JS is one measurement per layout, never per frame:

       --zh-ttag-from   The animation range, as the readable window rather than the
       --zh-ttag-to     whole passage. Nothing can be read while the strip is only
                        half in view, so a pass that starts on its first pixel is
                        already part way through by the time anyone can follow it.
                        That window is the view timeline's `contain` range: it opens
                        one section height into the entry and closes when the strip
                        starts to leave again.

                        The end is additionally cut to what a visitor can actually
                        reach. The section is the last one before the footer, so
                        the page can end while the strip still stands in the
                        viewport — which is why the copy used to stop mid word.

       --zh-ttag-view   The visible width the pass has to clear (see measure).

     Engines without scroll-driven animations (iOS below 26) keep an rAF mapping,
     but off those cached measurements: a scroll frame is one transform write and
     no layout read. */

  // Below this the pass would be a blur rather than a read, so a layout with almost
  // nothing under the section keeps a slower pass and accepts a cut off tail.
  var TTAG_MIN_REACH = 0.4;

  var TTAG_CSS_TIMELINE = !!(
    window.CSS &&
    window.CSS.supports &&
    window.CSS.supports('animation-timeline', 'view()')
  );

  /**
   * The scroll range one pass runs over, as a view-timeline range plus the same
   * numbers in scroll pixels for the fallback. Pure, so the arithmetic that used to
   * be wrong in two ways at once can be read (and tested) on its own.
   *
   * @param {DOMRect} box   - the section's box
   * @param {number} top    - current scroll position
   * @param {number} view   - viewport height
   * @param {number} below  - document height below the section
   * @returns {{name: string, start: number, span: number, reach: number}}
   */
  var ttagRange = function (box, top, view, below) {
    var sectionTop = box.top + top;

    // The readable window: `contain`, i.e. the stretch where the strip stands
    // completely in the viewport. It opens one section height into the entry, which
    // is exactly the point from which there is something to read.
    var name = 'contain';
    var span = view - box.height;
    var start = sectionTop - span;
    var reachable = below;

    if (span <= 0) {
      // Taller than the viewport: never contained, so there is no readable window
      // to sit in and the whole passage has to do.
      name = 'cover';
      span = view + box.height;
      start = sectionTop - view;
      reachable = box.height + below;
    }

    var reach = span > 0 ? Math.min(1, Math.max(TTAG_MIN_REACH, reachable / span)) : 0;

    return { name: name, start: start, span: span, reach: reach };
  };

  modules.ttag = function (scope) {
    if (!enabled('ttag')) return;
    $$('[data-zhm="ttag"]', scope).forEach(function (host) {
      if (!once(host, 'Ttag')) return;
      var strip = host.querySelector('.zhm-ttag__strip');
      if (!strip) return;
      var section = host.closest('.shopify-section') || host;

      var rangeStart = 0; // scroll position where the view range begins
      var rangeSpan = 1; // scroll distance of its reachable part
      var travel = 0; // px the strip moves across it: its whole overflow

      var measure = function () {
        var box = section.getBoundingClientRect();
        var top = window.scrollY || 0;
        var below = document.documentElement.scrollHeight - (box.bottom + top);
        var range = ttagRange(box, top, window.innerHeight, below);

        if (!range.reach) return; // nothing laid out (hidden section): nothing to map

        section.style.setProperty('--zh-ttag-from', range.name + ' 0%');
        section.style.setProperty('--zh-ttag-to', range.name + ' ' + (range.reach * 100).toFixed(2) + '%');

        rangeStart = range.start;
        rangeSpan = range.span * range.reach;

        // The visible width, not 100vw: that would include the scrollbar and push
        // the strip's last characters past the edge.
        var visible = strip.parentElement ? strip.parentElement.clientWidth : box.width;
        section.style.setProperty('--zh-ttag-view', visible + 'px');
        travel = Math.max(0, strip.offsetWidth - visible);
      };

      var frame = null;
      var observer = null;

      var apply = function () {
        frame = null;
        var p = ((window.scrollY || 0) - rangeStart) / rangeSpan;
        strip.style.transform = 'translateX(' + -(Math.min(1, Math.max(0, p)) * travel) + 'px)';
      };

      var onScroll = function () {
        if (!frame) frame = requestAnimationFrame(apply);
      };

      // The theme editor replaces whole sections, and nothing takes these listeners
      // down for us. A stale instance would keep measuring a detached node on every
      // resize, one more of them per reload.
      var teardown = function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onLayout);
        if (observer) observer.disconnect();
      };

      // Anything that moves the section or changes what is below it invalidates the
      // reach, so re-measure instead of trusting the load-time geometry.
      var onLayout = function () {
        if (!document.contains(section)) return teardown();
        measure();
        if (!TTAG_CSS_TIMELINE) apply();
      };

      // Arm first, then measure: .is-live is what shrink-wraps the strip to its
      // content, and the pass is exactly that width minus the visible one. Both
      // happen in the same task, so nothing is painted against a half state.
      strip.classList.add('is-live');
      measure();

      if (!TTAG_CSS_TIMELINE) {
        window.addEventListener('scroll', onScroll, { passive: true });
        apply();
      }
      window.addEventListener('resize', onLayout, { passive: true });
      // The page keeps growing after load (fonts, lazy media, app embeds), and the
      // reach is a function of what sits below the section.
      if (window.ResizeObserver) {
        observer = new window.ResizeObserver(onLayout);
        observer.observe(document.body);
      }
    });
  };

  /* Marquee + product track: the loops are pure CSS; here we only pause them
     while offscreen (battery) via IntersectionObserver. */
  modules.loopPause = function (scope) {
    $$('.zhm-marquee, [data-zhm-track]', scope).forEach(function (host) {
      if (!once(host, 'Loop')) return;
      var strip = host.querySelector('.zhm-marquee__track, .zhm-track__strip');
      if (!strip) return;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          strip.classList.toggle('is-paused', !entry.isIntersecting);
        });
      }).observe(host);
    });
  };

  /* Magnetic buttons: opt-in per element (never a global selector, so cart /
     checkout controls are untouched). Fine pointers only. */
  modules.magnetic = function (scope) {
    if (!enabled('magnetic') || !FINE) return;
    $$('[data-zhm="magnetic"]', scope).forEach(function (b) {
      if (!once(b, 'Magnet')) return;
      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        var dx = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        var dy = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        b.style.transform = 'translate(' + dx * 5 + 'px, ' + dy * 4 + 'px)';
      });
      b.addEventListener('pointerleave', function () {
        animate(b, { x: 0, y: 0 }, { duration: 0.55, ease: EXPO });
      });
      b.addEventListener('pointerdown', function () {
        b.style.scale = '0.97';
      });
      b.addEventListener('pointerup', function () {
        b.style.scale = '1';
      });
    });
  };

  /* ---------- page-level boot (runs exactly once, never on section reload) ---------- */

  /* Hero intro. Every animated unit gets its delay from its position in the DOM
     (index * hero_stagger_ms), so the order follows the markup and there are no
     hand maintained numbers anywhere. Initial hidden states come from CSS, each
     with a 3.5s failsafe. Four kinds of unit:

       intro-fade   opacity plus a small travel (header logo and nav, meta row,
                    CTA, note). The header is included on purpose: it used to be
                    exempt and therefore stood there while everything else was
                    still hidden.
       intro-word   word mask in the headline, and the older line masks
       intro-rule   hairline draws itself in via scaleX
       intro-type   plakat, fades in and hands off to its own typewriter

     Runs on the preloader handshake, so never while the curtain still travels, and
     ends with `zh:intro:done` so the sitewide layers that come last (speed dial) can
     hook themselves onto the tail of the same choreography. */
  var UNIT_SELECTOR = [
    '[data-zhm="intro-above"]',
    '[data-zhm="intro-fade"]',
    '[data-zhm="intro-word"] .zhm-w__in',
    '.zhm-line--intro .zhm-line__in',
    '[data-zhm="intro-rule"]',
    '[data-zhm="intro-type"]'
  ].join(',');

  var startTypewriter = function () {
    document.dispatchEvent(new CustomEvent('zh:hero:type'));
  };

  var introRan = false;

  /* Handover to whatever is meant to arrive AFTER the hero, currently the speed dial
     (sections/zh-speed-dial.liquid). Mirrors the preloader contract one step down the
     chain: a pending flag that is readable synchronously plus one event, dispatched
     exactly once on every exit path, so a cascade that never starts or dies mid flight
     can not strand a waiting element behind a hidden state. The flag is set here, at
     parse time, because this file runs from the head while the consumers are deferred
     scripts further down the body. */
  window.zhHomeIntro = { pending: true };

  var introSignalled = false;

  var signalIntroDone = function () {
    if (introSignalled) return;
    introSignalled = true;
    window.zhHomeIntro.pending = false;
    document.dispatchEvent(new CustomEvent('zh:intro:done'));
  };

  /* One unit of the cascade. The move itself is a CSS transition declared in
     assets/zh-home-motion.css; this only stamps the unit's delay, and the single
     class flip in runIntro then starts the whole cascade.

     Why not the Motion engine here any more: measured in ONE page load on a
     throttled mobile profile, the CSS driven curtain presented 60fps while this
     cascade, then driven by Motion, presented 27fps with hitches up to 205ms. The
     cause is not the engine's own cost but where it runs. Third party app scripts
     (TikTok, Clarity, Facebook, Klaviyo, Smile, PostHog) saturate the main thread
     exactly while the intro plays, and a main thread animation loses every frame
     they take. transform and opacity transitions run on the compositor and survive
     it. The engine stays loaded for everything below the fold, which animates on
     scroll and therefore never competes with the load.

     The entry direction is not read here at all: the direction classes are set
     pre-paint by sections/zh-preloader.liquid and already encode it in the initial
     states, so every unit's resting state is the same "transform: none, opacity: 1".
     That is what let this drop from five branches to one. */
  var armIntroUnit = function (el, delay) {
    var ms = Math.round(delay * 1000);
    el.style.transitionDelay = ms + 'ms';

    if (el.matches('[data-zhm="intro-type"]')) {
      // The plakat starts typing as it fades in, so nothing stands there fully typed
      // beforehand. Nothing waits for the typewriter, it runs as long as its text is.
      setTimeout(startTypewriter, ms);
    }
  };

  // The top area gets a wider gap so it reads as a sequence rather than one block.
  var ABOVE_STEP = 1.8;

  var runIntro = function () {
    if (introRan) return;
    introRan = true;

    var units = $$(UNIT_SELECTOR);

    if (!enabled('heroIntro')) {
      units.forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      startTypewriter();
      signalIntroDone();
      return;
    }

    var step = (intro.heroStaggerMs || 55) / 1000;
    // Accumulated, not index * step, so the wider top-area gap shifts everything
    // after it instead of leaving a hole.
    var cursor = 0;

    units.forEach(function (el) {
      armIntroUnit(el, cursor);
      cursor += step * (el.matches('[data-zhm="intro-above"]') ? ABOVE_STEP : 1);
    });

    if (!units.length) {
      signalIntroDone();
      return;
    }

    // One flip for the whole cascade, a frame after the delays are stamped, so every
    // unit transitions from a painted start state instead of jumping to its end.
    requestAnimationFrame(function () {
      root.classList.add('zhm-intro-run');
    });

    // The cascade is over once the LAST unit has landed, not once it started, so
    // anything queued behind it never overlaps the hero. A timeout and not
    // transitionend: an interrupted transition never fires that event, which would
    // strand the speed dial behind a hidden state. The duration comes from the same
    // custom property the CSS uses, so the two cannot drift.
    var longest = parseFloat(getComputedStyle(root).getPropertyValue('--zhm-intro-longest')) || 1050;
    setTimeout(signalIntroDone, Math.round(cursor * 1000 + longest) + 60);
  };

  var bootPage = function () {
    // Handshake with the preloader section. When a curtain is actually going to
    // play it owns the timing and tells us when it is gone; otherwise we start at
    // once. The extra timeout is a backstop: runIntro is idempotent, so a curtain
    // that dies mid-flight can never strand the hero behind hidden states.
    if (window.zhPreloader && window.zhPreloader.pending) {
      document.addEventListener('zh:preloader:done', runIntro, { once: true });
      // Backstop derived from the configured run time. A fixed 3.5s would have
      // started the hero mid curtain as soon as someone dialled the timings up.
      setTimeout(runIntro, intro.introFailsafeMs || 3500);
    } else {
      runIntro();
    }

    // Scroll progress bar.
    var bar = document.getElementById('zhm-progress-bar');
    if (bar && enabled('progress')) {
      scroll(animate(bar, { scaleX: [0, 1] }, { ease: 'linear' }));
    }

    // The negative cursor moved out of this layer: it is sitewide now and lives in
    // assets/zh-cursor.js, standalone so it does not need the Motion engine.
  };

  /* ---------- init ---------- */

  var init = function (scope) {
    Object.keys(modules).forEach(function (key) {
      modules[key](scope || document);
    });
  };

  bootPage();
  init(document);

  // Theme editor: re-run scoped for reloaded sections; guards keep it idempotent.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
